import { formatMoney, furthestStart, isEmailAddress } from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { Ref, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import { readCivilDate } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { continueLabel } from '../lib/two-step-payment'
import {
  AnswerLine,
  Ask,
  Button,
  Choice,
  DateInput,
  Field,
  Notice,
  PaymentStep,
  Question,
  TotalLine,
  useCheckoutConfirmation,
} from '../ui'
import { Challenge } from '../ui/Challenge'
import { DonorFields } from './DonorFields'
import { HOW_OFTEN, OFTEN, type Often } from './donate-page-helpers'

const money = formatMoney

/** When the card is first charged, said as the giver will read it. */
function whenCharged(startsOn: string, today: string): string {
  return startsOn === today
    ? 'Your card will be saved and the first gift taken today.'
    : `Nothing is charged today. Your card will be saved and first charged on ${readCivilDate(startsOn, 'long')}.`
}

export type StandingGiftAnswers = {
  amountCents: number
  cadence: 'monthly' | 'annual'
  startsOn: string
  forLabel: string | null
  email: string
}

/**
 * Once, monthly or yearly — asked only of an item that leaves it to the giver.
 * Picking does not advance, so the keyboard can read past the second option.
 */
export function HowOftenStep({
  stepRef,
  held,
  onAnswer,
}: {
  stepRef: Ref<HTMLDivElement>
  /** The answer being changed, marked when the question is asked again. */
  held: Often | null
  onAnswer: (often: Often) => void
}) {
  const [pending, setPending] = useState<string>(held ?? '')
  const picked = OFTEN.find((o) => o.value === pending)
  return (
    <Question ref={stepRef} ask={HOW_OFTEN} appearance="page">
      <Choice
        ariaLabel={HOW_OFTEN}
        options={OFTEN}
        value={pending}
        onValueChange={setPending}
      />
      <Button
        variant="secondary"
        disabled={!picked}
        onClick={() => picked && onAnswer(picked.value)}
      >
        Continue
      </Button>
    </Question>
  )
}

/**
 * The rest of a monthly or yearly gift, once what it is for and how much are
 * answered: when it starts, who is giving, the words they agree to, and the
 * card. The card is saved and the first gift is taken on the day they chose,
 * the way a membership starts.
 *
 * The answers are the page's: the name and address are the same ones a gift
 * given once asks, and all of them survive a question above being asked again.
 * They stay mounted while the card is showing, so Back finds them — and the
 * challenge — exactly as they were.
 */
export function StandingGiftStep({
  stepRef,
  orgId,
  itemId,
  forLabel,
  amountCents,
  cadence,
  today,
  startsOn,
  onStartsOn,
  name,
  onName,
  email,
  onEmail,
  onComplete,
}: {
  stepRef: RefObject<HTMLDivElement | null>
  orgId: string
  itemId: string
  forLabel: string | null
  amountCents: number
  cadence: 'monthly' | 'annual'
  /** The org's own today, which a start is quoted against. */
  today: string
  startsOn: string
  onStartsOn: (startsOn: string) => void
  name: string
  onName: (name: string) => void
  email: string
  onEmail: (email: string) => void
  onComplete: (checkoutId: string, answers: StandingGiftAnswers) => void
}) {
  const trpc = useTRPC()
  const challenge = useQuery(trpc.turnstile.siteKey.queryOptions())
  const [startOpen, setStartOpen] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [spentAt, setSpentAt] = useState(0)

  /* Opening and closing the start question swaps one question for another
     inside this step, where the page does not see it: the question now in
     hand takes focus here, the way the page moves it between its own. */
  const wasOpen = useRef(startOpen)
  useEffect(() => {
    if (wasOpen.current !== startOpen) stepRef.current?.focus()
    wasOpen.current = startOpen
  }, [startOpen, stepRef])

  const consent = useQuery(
    trpc.donation.recurringGiftConsent.queryOptions({
      shortId: orgId,
      itemId,
      amountCents,
      cadence,
      startsOn,
    }),
  )
  const checkout = useMutation(
    trpc.donation.recurringGiftCheckout.mutationOptions(),
  )
  const confirmation = useCheckoutConfirmation({
    onComplete: (checkoutId) =>
      onComplete(checkoutId, {
        amountCents,
        cadence,
        startsOn,
        forLabel,
        email: email.trim(),
      }),
  })
  /* A changed answer discards the open checkout: it was opened for the answers
     as they stood, and the next Continue opens one for what is then said. */
  const discardCheckout = () => {
    checkout.reset()
    confirmation.reset()
  }

  const each = cadence === 'annual' ? 'each year' : 'each month'
  const ready =
    name.trim().length > 0 &&
    isEmailAddress(email) &&
    Boolean(consent.data?.consentText) &&
    (!challenge.data?.siteKey || token !== null)

  const submit = () => {
    if (!ready) return
    checkout.mutate(
      {
        shortId: orgId,
        itemId,
        amountCents,
        cadence,
        startsOn,
        consentText: consent.data?.consentText ?? '',
        donorName: name.trim(),
        donorEmail: email.trim(),
        challenge: token,
      },
      {
        /* A token is good once, so it is spent here whatever the answer: the
           next Continue waits for a fresh one rather than sending this one
           again. */
        onSettled: () => {
          setToken(null)
          setSpentAt(Date.now())
        },
      },
    )
  }

  if (startOpen) {
    return (
      <Question
        ref={stepRef}
        ask="When should the gift start?"
        appearance="page"
      >
        <Field label="Starts on">
          <DateInput
            value={startsOn}
            min={today}
            max={furthestStart(today)}
            onChange={(e) => onStartsOn(e.target.value)}
          />
        </Field>
        <p className="donate__fine">{whenCharged(startsOn, today)}</p>
        <Button variant="secondary" onClick={() => setStartOpen(false)}>
          Continue
        </Button>
      </Question>
    )
  }

  return (
    <>
      {/* Folded in answered: today is the start most givers mean, and the
        line is where it is changed. */}
      <AnswerLine
        appearance="page"
        label="Starts"
        value={startsOn === today ? 'Today' : readCivilDate(startsOn, 'long')}
        changeLabel="Change when the gift starts"
        onChange={() => {
          discardCheckout()
          setStartOpen(true)
        }}
      />
      <Question ref={stepRef} ask="Who is giving?" appearance="page">
        <DonorFields
          name={name}
          onName={onName}
          email={email}
          onEmail={onEmail}
        />

        <div className="donate__total">
          <TotalLine
            label={forLabel ?? 'Your gift'}
            amount={money(amountCents)}
            note={each}
          />
          {startsOn !== today && (
            <TotalLine label="Due today" amount={money(0)} emphasis="total" />
          )}
          <TotalLine
            label="First gift"
            amount={money(amountCents)}
            note={`on ${readCivilDate(startsOn, 'long')}`}
          />
        </div>

        {consent.data?.consentText && <Ask>{consent.data.consentText}</Ask>}
        {consent.error && <Notice tone="error">{consent.error.message}</Notice>}

        <Challenge
          siteKey={challenge.data?.siteKey ?? null}
          onToken={setToken}
          spentAt={spentAt}
        />

        {checkout.error && (
          <Notice tone="error">{checkout.error.message}</Notice>
        )}
        <Button onClick={submit} disabled={!ready || checkout.isPending}>
          {continueLabel(checkout.isPending)}
        </Button>
        <p className="donate__fine">
          Next, your card. {whenCharged(startsOn, today)}
        </p>
        {checkout.data && checkout.variables && (
          <PaymentStep
            orgId={orgId}
            card={checkout.data.card}
            what={forLabel ?? 'Your gift'}
            who={`Giving as ${checkout.variables.donorName}, ${checkout.variables.donorEmail}`}
            amount={money(checkout.variables.amountCents)}
            amountNote={`${each} from ${readCivilDate(checkout.variables.startsOn, 'long')}`}
            payLabel="Save card"
            fine={whenCharged(checkout.variables.startsOn, today)}
            confirmation={confirmation}
            onBack={discardCheckout}
          />
        )}
      </Question>
    </>
  )
}
